import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import { Button, Text } from '@musa.tech/design-system';
import { useFilterOptions } from '@/hooks/useFilterOptions';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskEstablishmentName, maskGeneratorName } from '@/utils/demoMode';
import { AppliedFilterChips, type FilterChip } from '@/components/shared/filters/AppliedFilterChips';
import { FilterDropdownButton } from '@/components/shared/filters/FilterDropdownButton';
import { OptionsFilterDropdown, type FilterOption } from '@/components/shared/filters/OptionsFilterDropdown';
import { formatBillingPeriodLabel, getBillingPeriodOptions } from './billingPeriodOptions';
import { MEASUREMENT_STATUS_OPTIONS } from './contestacao/measurementStatus';
import { computeVisibleFilterCount } from './filterOverflow';
import { PillRange } from '@/components/shared/filters/PillRange';

export interface DateRangeValue {
  start: Date | null;
  end: Date | null;
}

type FinanceFilterKey =
  | 'tipo'
  | 'competencia'
  | 'vencimento'
  | 'status_fatura'
  | 'status_medicao'
  | 'estabelecimento'
  | 'gerador';

const INVOICE_STATUS_OPTIONS: FilterOption[] = [
  { value: 'A VENCER', label: 'A vencer' },
  { value: 'VENCE HOJE', label: 'Vence hoje' },
  { value: 'RECEBIDO', label: 'Recebido' },
  { value: 'ATRASADO', label: 'Atrasado' },
  { value: 'CANCELADO', label: 'Cancelado' },
];

/** `'FATURA'` (tem OS na Omie) e/ou `'MEDICAO'` (ainda em análise interna, sem OS). */
const RECORD_TYPE_OPTIONS: FilterOption[] = [
  { value: 'FATURA', label: 'Fatura' },
  { value: 'MEDICAO', label: 'Medição' },
];

const FILTER_ROW_GAP_PX = 8; // gap-2
const MORE_BUTTON_RESERVED_PX = 104; // "Ver menos" + chevron, com folga

/**
 * Quantos filtros (na ordem de `itemCount`) cabem, lado a lado, na largura atual do
 * container — sem cortar nenhum no meio. Mede a largura real de cada pill (numa cópia
 * invisível, fora do fluxo) e do próprio container a cada render/resize.
 *
 * Por que medir em vez de fixar por breakpoint: o pedido era "tudo numa linha só enquanto
 * couber; quebra só quando a tela ficar pequena de verdade" — um corte por breakpoint fixo
 * sempre deixa uma faixa de largura em que caberiam mais filtros do que o breakpoint mostra
 * (ou o contrário). Diferente da tentativa anterior (24/08/2026), que cortava com
 * `max-height` + `overflow: hidden` sem saber onde a borda de um pill caía: aqui nunca se
 * corta um item pela metade — só se decide, por medição, quantos itens inteiros cabem.
 */
function useFilterOverflow(itemCount: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  const recompute = () => {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.offsetWidth;
    // Sem largura real ainda (primeiro paint, ou ambiente sem layout como o jsdom dos
    // testes) — mantém o valor atual em vez de assumir que nada cabe.
    if (containerWidth <= 0) return;

    const widths = measureRefs.current
      .slice(0, itemCount)
      .map((el) => el?.offsetWidth ?? 0);

    const fitCount = computeVisibleFilterCount(
      widths,
      containerWidth,
      FILTER_ROW_GAP_PX,
      MORE_BUTTON_RESERVED_PX,
    );
    // Piso de 1: reservar espaço pro "Ver mais" não pode zerar a barra inteira numa tela
    // estreita — melhor um filtro sozinho (e o "Ver mais" quebrando pra outra linha, se
    // preciso) do que uma barra sem nenhum filtro visível.
    setVisibleCount(itemCount > 0 ? Math.max(fitCount, 1) : 0);
  };

  // Reage a resize contínuo da janela/container.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCount]);

  // Reage a mudança de largura de um pill sem mudar a contagem (ex.: contador "(2)" no
  // rótulo ao selecionar um valor).
  useLayoutEffect(() => {
    recompute();
  });

  return { containerRef, measureRefs, visibleCount: Math.min(visibleCount, itemCount) };
}

function toDateInput(date: Date | null): string {
  return date ? format(date, 'yyyy-MM-dd') : '';
}

function fromDateInput(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

/** Range sem nenhuma extremidade vale como filtro ausente. */
function normalizeRange(range: DateRangeValue): DateRangeValue | null {
  return range.start || range.end ? range : null;
}

interface FinanceFilterBarProps {
  dueDateRange: DateRangeValue | null;
  /** Competências específicas (`YYYY-MM`) — não um intervalo. Ver `billingPeriodOptions.ts`. */
  billingPeriods: string[];
  establishmentIds: number[];
  generatorIds: string[];
  statusFilter: string;
  releaseStatuses: string[];
  /** `'FATURA'` e/ou `'MEDICAO'`. Os dois juntos (ou vazio) têm o mesmo efeito de ausente. */
  types: string[];
  onDueDateRangeChange: (range: DateRangeValue | null) => void;
  onBillingPeriodsChange: (periods: string[]) => void;
  onEstablishmentIdsChange: (ids: number[]) => void;
  onGeneratorIdsChange: (ids: string[]) => void;
  onStatusFilterChange: (status: string) => void;
  onReleaseStatusesChange: (statuses: string[]) => void;
  onTypesChange: (types: string[]) => void;
  onClearFilters: () => void;
}

export const FinanceFilterBar: React.FC<FinanceFilterBarProps> = ({
  dueDateRange,
  billingPeriods,
  establishmentIds,
  generatorIds,
  statusFilter,
  releaseStatuses,
  types,
  onDueDateRangeChange,
  onBillingPeriodsChange,
  onEstablishmentIdsChange,
  onGeneratorIdsChange,
  onStatusFilterChange,
  onReleaseStatusesChange,
  onTypesChange,
  onClearFilters,
}) => {
  const { isDemoMode } = useDemoMode();
  const { trackButtonClick } = useAnalytics();
  const { establishments, generators } = useFilterOptions({
    selectedGeneratorExternalIds: generatorIds.length > 0 ? generatorIds : undefined,
  });

  const [expanded, setExpanded] = useState(false);

  const showGeneratorFilter = generators.length > 1;
  const showEstablishmentFilter = establishments.length > 1;
  // Calculado uma vez: a janela de meses disponíveis não muda durante a vida do componente.
  const billingPeriodOptions = useMemo(() => getBillingPeriodOptions(), []);

  const establishmentOptions = useMemo<FilterOption[]>(
    () =>
      establishments.map((establishment) => ({
        value: establishment.id_new_engine_generators,
        label: maskEstablishmentName(establishment.establishment_name, isDemoMode),
      })),
    [establishments, isDemoMode],
  );

  const generatorOptions = useMemo<FilterOption[]>(
    () =>
      generators.map((generator) => ({
        value: generator.generator_id,
        label: maskGeneratorName(generator.generator_name, isDemoMode),
      })),
    [generators, isDemoMode],
  );

  const selectedStatuses = useMemo(
    () => (statusFilter && statusFilter !== 'all' ? [statusFilter] : []),
    [statusFilter],
  );

  const trackFilter = (field: string) =>
    trackButtonClick({ buttonLabel: `financeiro_filtrar_${field}` });

  const changeDueDate = (next: DateRangeValue) => {
    trackFilter('vencimento');
    onDueDateRangeChange(normalizeRange(next));
  };

  // Ordem de prioridade: quando não cabe tudo numa linha, os últimos da lista são os que
  // ficam atrás do "Ver mais".
  const filterEntries = useMemo(() => {
    const entries: { key: FinanceFilterKey; hasValue: boolean; node: React.ReactNode }[] = [];

    if (showEstablishmentFilter) {
      entries.push({
        key: 'estabelecimento',
        hasValue: establishmentIds.length > 0,
        node: (
          <OptionsFilterDropdown
            label="Estabelecimento"
            options={establishmentOptions}
            value={establishmentIds.map(String)}
            searchPlaceholder="Buscar estabelecimento..."
            emptyMessage="Nenhum estabelecimento disponível."
            onChange={(values) => {
              trackFilter('estabelecimento');
              onEstablishmentIdsChange(values.map(Number));
            }}
          />
        ),
      });
    }

    entries.push({
      key: 'tipo',
      hasValue: types.length > 0,
      node: (
        <OptionsFilterDropdown
          label="Tipo"
          options={RECORD_TYPE_OPTIONS}
          value={types}
          onChange={(values) => {
            trackFilter('tipo');
            onTypesChange(values);
          }}
        />
      ),
    });

    entries.push({
      key: 'competencia',
      hasValue: billingPeriods.length > 0,
      node: (
        <OptionsFilterDropdown
          label="Competência"
          options={billingPeriodOptions}
          value={billingPeriods}
          onChange={(values) => {
            trackFilter('competencia');
            onBillingPeriodsChange(values);
          }}
        />
      ),
    });

    entries.push({
      key: 'vencimento',
      hasValue: Boolean(dueDateRange?.start || dueDateRange?.end),
      node: (
        <FilterDropdownButton
          label="Vencimento"
          count={[dueDateRange?.start, dueDateRange?.end].filter(Boolean).length}
        >
          <PillRange
            type="date"
            from={toDateInput(dueDateRange?.start ?? null)}
            to={toDateInput(dueDateRange?.end ?? null)}
            onFromChange={(value) =>
              changeDueDate({ start: fromDateInput(value), end: dueDateRange?.end ?? null })
            }
            onToChange={(value) =>
              changeDueDate({ start: dueDateRange?.start ?? null, end: fromDateInput(value) })
            }
          />
        </FilterDropdownButton>
      ),
    });

    entries.push({
      key: 'status_fatura',
      hasValue: statusFilter !== 'all',
      node: (
        <OptionsFilterDropdown
          label="Status da fatura"
          options={INVOICE_STATUS_OPTIONS}
          value={selectedStatuses}
          multiple={false}
          onChange={(values) => {
            trackFilter('status_fatura');
            onStatusFilterChange(values[0] ?? 'all');
          }}
        />
      ),
    });

    entries.push({
      key: 'status_medicao',
      hasValue: releaseStatuses.length > 0,
      node: (
        <OptionsFilterDropdown
          label="Status medição"
          options={MEASUREMENT_STATUS_OPTIONS}
          value={releaseStatuses}
          onChange={(values) => {
            trackFilter('estado_medicao');
            onReleaseStatusesChange(values);
          }}
        />
      ),
    });

    if (showGeneratorFilter) {
      entries.push({
        key: 'gerador',
        hasValue: generatorIds.length > 0,
        node: (
          <OptionsFilterDropdown
            label="Gerador"
            options={generatorOptions}
            value={generatorIds}
            searchPlaceholder="Buscar gerador..."
            emptyMessage="Nenhum gerador disponível."
            onChange={(values) => {
              trackFilter('gerador');
              onGeneratorIdsChange(values);
            }}
          />
        ),
      });
    }

    return entries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showEstablishmentFilter,
    showGeneratorFilter,
    establishmentIds,
    generatorIds,
    billingPeriods,
    billingPeriodOptions,
    dueDateRange,
    statusFilter,
    releaseStatuses,
    types,
    establishmentOptions,
    generatorOptions,
  ]);

  const { containerRef, measureRefs, visibleCount } = useFilterOverflow(filterEntries.length);

  // Um filtro ativo escondido atrás do "Ver mais" não pode desaparecer da barra sem o
  // gerador saber — trava tudo visível até ele desmarcar (mesmo raciocínio de antes, agora
  // aplicado à contagem medida em vez de uma linha fixa).
  const hasActiveHiddenFilter = filterEntries
    .slice(visibleCount)
    .some((entry) => entry.hasValue);
  const showAll = expanded || hasActiveHiddenFilter || visibleCount >= filterEntries.length;
  const visibleEntries = showAll ? filterEntries : filterEntries.slice(0, visibleCount);
  // Só cabe alternar quando colapsar de fato esconderia algo — sem isso um "Ver menos" que
  // não recolhe nada (tela cresceu, ou há filtro ativo escondido) fica bloqueado exibido.
  const canToggle = visibleCount < filterEntries.length && !hasActiveHiddenFilter;

  const chips = useMemo<FilterChip<FinanceFilterKey>[]>(() => {
    const result: FilterChip<FinanceFilterKey>[] = [];

    types.forEach((type) => {
      const option = RECORD_TYPE_OPTIONS.find((o) => o.value === type);
      result.push({
        key: 'tipo',
        value: type,
        label: 'Tipo',
        display: option?.label ?? type,
      });
    });

    billingPeriods.forEach((period) => {
      result.push({
        key: 'competencia',
        value: period,
        label: 'Competência',
        display: formatBillingPeriodLabel(period),
      });
    });

    if (dueDateRange?.start) {
      result.push({
        key: 'vencimento',
        value: 'de',
        label: 'Vencimento de',
        display: format(dueDateRange.start, 'dd/MM/yyyy'),
      });
    }
    if (dueDateRange?.end) {
      result.push({
        key: 'vencimento',
        value: 'ate',
        label: 'Vencimento até',
        display: format(dueDateRange.end, 'dd/MM/yyyy'),
      });
    }

    selectedStatuses.forEach((status) => {
      const option = INVOICE_STATUS_OPTIONS.find((o) => o.value === status);
      result.push({
        key: 'status_fatura',
        label: 'Status da fatura',
        display: option?.label ?? status,
      });
    });

    releaseStatuses.forEach((status) => {
      const option = MEASUREMENT_STATUS_OPTIONS.find((o) => o.value === status);
      result.push({
        key: 'status_medicao',
        value: status,
        label: 'Status medição',
        display: option?.label ?? status,
      });
    });

    establishmentIds.forEach((id) => {
      const option = establishmentOptions.find((o) => o.value === String(id));
      result.push({
        key: 'estabelecimento',
        value: String(id),
        label: 'Estabelecimento',
        display: option?.label ?? String(id),
      });
    });

    generatorIds.forEach((id) => {
      const option = generatorOptions.find((o) => o.value === id);
      result.push({
        key: 'gerador',
        value: id,
        label: 'Gerador',
        display: option?.label ?? id,
      });
    });

    return result;
  }, [
    types,
    billingPeriods,
    dueDateRange,
    selectedStatuses,
    releaseStatuses,
    establishmentIds,
    generatorIds,
    establishmentOptions,
    generatorOptions,
  ]);

  // Remove um valor pontual (chip) ou o filtro inteiro (sem `value`), pelo par (chave, valor)
  // que o próprio chip carrega — mesmo contrato do AppliedFilterChips do Dashboard.
  const handleRemoveFilter = (key: FinanceFilterKey, value?: string) => {
    switch (key) {
      case 'tipo':
        if (value) onTypesChange(types.filter((t) => t !== value));
        break;
      case 'competencia':
        if (value) onBillingPeriodsChange(billingPeriods.filter((p) => p !== value));
        break;
      case 'vencimento':
        onDueDateRangeChange(
          normalizeRange({
            start: value === 'de' ? null : (dueDateRange?.start ?? null),
            end: value === 'ate' ? null : (dueDateRange?.end ?? null),
          }),
        );
        break;
      case 'status_fatura':
        onStatusFilterChange('all');
        break;
      case 'status_medicao':
        if (value) onReleaseStatusesChange(releaseStatuses.filter((v) => v !== value));
        break;
      case 'estabelecimento':
        if (value) onEstablishmentIdsChange(establishmentIds.filter((id) => String(id) !== value));
        break;
      case 'gerador':
        if (value) onGeneratorIdsChange(generatorIds.filter((v) => v !== value));
        break;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-start gap-2 rounded-lg bg-white px-4 py-3 md:flex-row">
        <Text
          variant="label"
          size="small"
          className="shrink-0 whitespace-nowrap py-2 text-neutral-600"
        >
          Filtrar por
        </Text>

        <div
          ref={containerRef}
          data-testid="financeiro-filtros-pills"
          className="relative flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2"
        >
          {/* Cópia invisível, fora do fluxo, só pra medir a largura real de cada pill —
              inclusive os que estão escondidos agora — sem afetar o layout visível. */}
          <div
            aria-hidden
            className="pointer-events-none invisible absolute left-0 top-0 flex flex-nowrap gap-2"
          >
            {filterEntries.map((entry, index) => (
              <div key={entry.key} ref={(el) => (measureRefs.current[index] = el)}>
                {entry.node}
              </div>
            ))}
          </div>

          {visibleEntries.map((entry) =>
            React.cloneElement(entry.node as React.ReactElement, { key: entry.key }),
          )}

          {canToggle && (
            <Button
              variant="link"
              size="sm"
              className="shrink-0 gap-1 font-inter font-normal text-primary-600 hover:text-primary-700"
              onClick={() => setExpanded((previous) => !previous)}
            >
              {showAll ? 'Ver menos' : 'Ver mais'}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${showAll ? 'rotate-180' : ''}`}
              />
            </Button>
          )}
        </div>
      </div>

      <AppliedFilterChips chips={chips} onRemove={handleRemoveFilter} onClearAll={onClearFilters} />
    </div>
  );
};
